import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  GripVertical,
  Loader2,
  Package,
  Scissors,
  Upload,
} from "lucide-react";
import JSZip from "jszip";
import { cloneArrayBuffer } from "../lib/file";
import {
  identityPageOrder,
  pdfBaseName,
  pdfPageCount,
  renderAllPdfPageThumbs,
  segmentsFromPageOrder,
  splitPartFileName,
  splitPdfPages,
  type PdfThumb,
} from "../lib/pdfOps";
import { saveAndShareBytes } from "../lib/shareText";
import { ToolPageShell } from "./ToolPages";

type Props = {
  initialBuffer?: ArrayBuffer | null;
  initialName?: string;
  onClose: () => void;
  onMinimize?: () => void;
};

type LoadedPdf = {
  buffer: ArrayBuffer;
  name: string;
  pageCount: number;
};

function moveIndex<T>(list: T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) {
    return list;
  }
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

export function PdfSplitPage({
  initialBuffer = null,
  initialName,
  onClose,
  onMinimize,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragPageRef = useRef<number | null>(null);
  const [doc, setDoc] = useState<LoadedPdf | null>(null);
  const [thumbs, setThumbs] = useState<(PdfThumb | null)[]>([]);
  const [thumbsBusy, setThumbsBusy] = useState(false);
  const [pageOrder, setPageOrder] = useState<number[]>([]);
  const [splitsAfter, setSplitsAfter] = useState<Set<number>>(() => new Set());
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (bootstrappedRef.current || !initialBuffer) return;
    bootstrappedRef.current = true;
    let cancelled = false;
    void (async () => {
      setBusy(true);
      try {
        const pages = await pdfPageCount(initialBuffer);
        if (cancelled) return;
        const pageCount = Math.max(1, pages);
        setDoc({
          buffer: initialBuffer,
          name: initialName || "document.pdf",
          pageCount,
        });
        setPageOrder(identityPageOrder(pageCount));
        setSplitsAfter(new Set());
        setSelected(new Set());
      } catch (err) {
        if (!cancelled) {
          setHint(
            err instanceof Error ? err.message : "خواندن PDF ناموفق بود"
          );
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [initialBuffer, initialName]);

  const segments = useMemo(
    () => segmentsFromPageOrder(pageOrder, splitsAfter),
    [pageOrder, splitsAfter]
  );

  useEffect(() => {
    if (!doc) {
      setThumbs([]);
      return;
    }
    let cancelled = false;
    setThumbsBusy(true);
    setThumbs(new Array(doc.pageCount).fill(null));
    void (async () => {
      try {
        await renderAllPdfPageThumbs(doc.buffer, 168, (page, thumb, total) => {
          if (cancelled) return;
          setThumbs((prev) => {
            const next =
              prev.length === total ? [...prev] : new Array(total).fill(null);
            next[page - 1] = thumb;
            return next;
          });
        });
      } catch (err) {
        if (!cancelled) {
          setHint(
            err instanceof Error ? err.message : "خطا در ساخت بندانگشتی"
          );
        }
      } finally {
        if (!cancelled) setThumbsBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [doc]);

  const loadFile = async (file: File) => {
    setBusy(true);
    setHint(null);
    try {
      const buffer = cloneArrayBuffer(await file.arrayBuffer());
      const pages = await pdfPageCount(buffer);
      if (pages < 1) throw new Error("این PDF صفحه‌ای ندارد.");
      setDoc({
        buffer,
        name: file.name || "document.pdf",
        pageCount: pages,
      });
      setPageOrder(identityPageOrder(pages));
      setSplitsAfter(new Set());
      setSelected(new Set());
    } catch (err) {
      setHint(err instanceof Error ? err.message : "خواندن PDF ناموفق بود");
    } finally {
      setBusy(false);
    }
  };

  const toggleSplitAfter = (page: number) => {
    if (!doc || pageOrder.length < 2) return;
    const idx = pageOrder.indexOf(page);
    if (idx < 0 || idx >= pageOrder.length - 1) return;
    setSplitsAfter((prev) => {
      const next = new Set(prev);
      if (next.has(page)) next.delete(page);
      else next.add(page);
      return next;
    });
  };

  const clearSplits = () => setSplitsAfter(new Set());
  const clearSelection = () => setSelected(new Set());

  const toggleSelect = (page: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(page)) next.delete(page);
      else next.add(page);
      return next;
    });
  };

  const reorderByPage = (fromPage: number, toPage: number) => {
    if (fromPage === toPage) return;
    setPageOrder((prev) => {
      const from = prev.indexOf(fromPage);
      const to = prev.indexOf(toPage);
      if (from < 0 || to < 0) return prev;
      return moveIndex(prev, from, to);
    });
  };

  const movePage = (page: number, delta: -1 | 1) => {
    setPageOrder((prev) => {
      const from = prev.indexOf(page);
      if (from < 0) return prev;
      return moveIndex(prev, from, from + delta);
    });
  };

  const runSave = async () => {
    if (!doc) {
      setHint("ابتدا یک PDF انتخاب کنید.");
      return;
    }
    if (segments.length < 2) {
      setHint("حداقل یک نقطهٔ برش بین صفحات بگذارید.");
      return;
    }
    setBusy(true);
    setHint(null);
    try {
      const base = pdfBaseName(doc.name);
      const zip = new JSZip();
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i]!;
        const bytes = await splitPdfPages(doc.buffer, seg.pages);
        zip.file(splitPartFileName(base, i + 1), bytes);
      }
      const zipBytes = await zip.generateAsync({ type: "uint8array" });
      await saveAndShareBytes(
        zipBytes,
        `${base}-parts.zip`,
        "application/zip"
      );
      setHint(
        `${segments.length.toLocaleString("fa-IR")} بخش در فایل ZIP آماده شد.`
      );
    } catch (err) {
      setHint(err instanceof Error ? err.message : "خطا در تفکیک");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ToolPageShell
      title="تفکیک صفحات"
      toolId="pdf-split"
      onClose={onClose}
      onMinimize={onMinimize}
      busy={busy || thumbsBusy}
      footer={
        !doc ? (
          <button
            type="button"
            className="primary-btn"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy ? (
              <Loader2 size={16} className="spin" />
            ) : (
              <Upload size={16} />
            )}
            انتخاب PDF
          </button>
        ) : (
          <>
            {selected.size > 0 && (
              <button
                type="button"
                className="ghost-btn"
                disabled={busy}
                onClick={clearSelection}
              >
                پاک‌کردن انتخاب
                {" "}
                ({selected.size.toLocaleString("fa-IR")})
              </button>
            )}
            {splitsAfter.size > 0 && (
              <button
                type="button"
                className="ghost-btn"
                disabled={busy}
                onClick={clearSplits}
              >
                پاک‌کردن برش‌ها
              </button>
            )}
            <button
              type="button"
              className="primary-btn"
              disabled={busy || segments.length < 2}
              onClick={() => void runSave()}
            >
              {busy ? (
                <Loader2 size={16} className="spin" />
              ) : (
                <Package size={16} />
              )}
              ذخیره ZIP
            </button>
          </>
        )
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) void loadFile(f);
        }}
      />

      {!doc ? (
        <div className="pdf-split-empty">
          {hint && <p className="settings-hint">{hint}</p>}
        </div>
      ) : (
        <>
          <div className="pdf-split-meta">
            <div>
              <strong>{doc.name}</strong>
              <span>
                {doc.pageCount.toLocaleString("fa-IR")} صفحه
                {thumbsBusy ? " · در حال ساخت بندانگشتی…" : ""}
              </span>
            </div>
            <button
              type="button"
              className="icon-btn compact"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              <Upload size={14} />
              تعویض
            </button>
          </div>

          <p className="settings-hint">
            بندانگشتی را برای انتخاب بزنید. با دستگیره جابه‌جا کنید یا دکمه‌های
            بالا/پایین. روی خط چین «برش از اینجا» را بزنید. ذخیره، یک ZIP با
            نام‌هایی مثل{" "}
            <code>{splitPartFileName(doc.name, 1)}</code> می‌سازد.
          </p>

          {segments.length > 1 && (
            <div className="pdf-split-parts" aria-live="polite">
              {segments.map((seg, i) => {
                const start = pageOrder.indexOf(seg.pages[0]!) + 1;
                const end = pageOrder.indexOf(seg.pages[seg.pages.length - 1]!) + 1;
                return (
                  <span
                    key={`${seg.pages.join("-")}-${i}`}
                    className="pdf-split-part-pill"
                  >
                    بخش {(i + 1).toLocaleString("fa-IR")}: ترتیب{" "}
                    {start.toLocaleString("fa-IR")}
                    {start !== end
                      ? ` تا ${end.toLocaleString("fa-IR")}`
                      : ""}
                  </span>
                );
              })}
            </div>
          )}

          <ol className="pdf-split-list">
            {pageOrder.map((page, i) => {
              const thumb = thumbs[page - 1];
              const orderNum = i + 1;
              const isLast = i === pageOrder.length - 1;
              const splitHere = !isLast && splitsAfter.has(page);
              const isSelected = selected.has(page);
              return (
                <li
                  key={page}
                  className={
                    isSelected
                      ? "pdf-split-item is-selected"
                      : "pdf-split-item"
                  }
                  data-page={page}
                  draggable
                  onDragStart={() => {
                    dragPageRef.current = page;
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (dragPageRef.current != null) {
                      reorderByPage(dragPageRef.current, page);
                    }
                    dragPageRef.current = null;
                  }}
                  onTouchStart={() => {
                    dragPageRef.current = page;
                  }}
                  onTouchMove={(e) => {
                    const t = e.touches[0];
                    if (!t || dragPageRef.current == null) return;
                    const el = document.elementFromPoint(t.clientX, t.clientY);
                    const row = el?.closest(".pdf-split-item") as HTMLElement | null;
                    const targetPage = Number(row?.dataset.page);
                    if (
                      Number.isFinite(targetPage) &&
                      targetPage > 0 &&
                      targetPage !== dragPageRef.current
                    ) {
                      reorderByPage(dragPageRef.current, targetPage);
                      dragPageRef.current = targetPage;
                    }
                  }}
                  onTouchEnd={() => {
                    dragPageRef.current = null;
                  }}
                >
                  <div className="pdf-split-page">
                    <span className="pdf-split-grip" aria-hidden>
                      <GripVertical size={16} />
                    </span>

                    <button
                      type="button"
                      className={
                        isSelected
                          ? "pdf-split-thumb is-selected"
                          : "pdf-split-thumb"
                      }
                      aria-pressed={isSelected}
                      aria-label={
                        isSelected
                          ? `لغو انتخاب صفحه ترتیب ${orderNum}`
                          : `انتخاب صفحه ترتیب ${orderNum}`
                      }
                      disabled={busy}
                      onClick={() => toggleSelect(page)}
                    >
                      {thumb ? (
                        <img src={thumb.dataUrl} alt="" />
                      ) : (
                        <span className="pdf-split-thumb-ph">
                          {thumbsBusy ? "…" : "PDF"}
                        </span>
                      )}
                      <span className="pdf-split-order-badge" aria-hidden>
                        {orderNum.toLocaleString("fa-IR")}
                      </span>
                    </button>

                    <div className="pdf-split-page-meta">
                      <span className="pdf-split-page-num">
                        ترتیب {orderNum.toLocaleString("fa-IR")}
                      </span>
                      {page !== orderNum && (
                        <span className="pdf-split-page-orig">
                          صفحه اصلی {page.toLocaleString("fa-IR")}
                        </span>
                      )}
                    </div>

                    <div className="pdf-split-reorder">
                      <button
                        type="button"
                        className="icon-btn compact"
                        aria-label={`جابه‌جایی صفحه ترتیب ${orderNum} به بالا`}
                        disabled={busy || i === 0}
                        onClick={() => movePage(page, -1)}
                      >
                        <ChevronUp size={14} />
                      </button>
                      <button
                        type="button"
                        className="icon-btn compact"
                        aria-label={`جابه‌جایی صفحه ترتیب ${orderNum} به پایین`}
                        disabled={busy || isLast}
                        onClick={() => movePage(page, 1)}
                      >
                        <ChevronDown size={14} />
                      </button>
                    </div>
                  </div>

                  {!isLast && (
                    <div
                      className={
                        splitHere
                          ? "pdf-split-rail is-active"
                          : "pdf-split-rail"
                      }
                    >
                      <span className="pdf-split-rail-line" aria-hidden />
                      <button
                        type="button"
                        className={
                          splitHere
                            ? "pdf-split-rail-btn is-on"
                            : "pdf-split-rail-btn"
                        }
                        aria-pressed={splitHere}
                        aria-label={
                          splitHere
                            ? `حذف برش بعد از ترتیب ${orderNum}`
                            : `برش بعد از ترتیب ${orderNum}`
                        }
                        disabled={busy}
                        onClick={() => toggleSplitAfter(page)}
                      >
                        <Scissors size={13} />
                        {splitHere ? "حذف برش" : "برش از اینجا"}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ol>

          {hint && <p className="settings-hint">{hint}</p>}
        </>
      )}
    </ToolPageShell>
  );
}
